/**
 * ReplyQuote — inline quote chip rendered ABOVE the message body for
 * any message that has `replyTo`. WhatsApp-style: left/right border
 * accent, sender name, snippet preview, click → scroll-to-original.
 *
 * Phase 2 (owner-spec 2026-05-31). Wired by each chat consumer:
 *   <MessageBubble> → renderMedia / text body
 *   prepended by <ReplyQuote msg={msg} onClickOriginal={...} />
 *
 * The parent owns the scroll-to-original logic (it knows the messageRefs
 * map). We just emit `onClickOriginal(originalId)` on tap.
 */

import React from 'react';

const MEDIA_LABELS = {
  image: '📷 תמונה',
  video: '🎬 וידאו',
  audio: '🎵 אודיו',
  voice: '🎤 הקלטה',
  document: '📄 מסמך',
};

/**
 * Try to enrich the saved `replyTo` snapshot from the live messages
 * array. Inbound replies from Infobip only carry the original id;
 * outbound replies carry a full snapshot. Doing the lookup on render
 * means the quote stays fresh even if the original was edited.
 */
const resolveOriginal = (replyTo, messages) => {
  if (!replyTo) return null;
  // If we have a full snapshot, use it verbatim.
  if (replyTo.body || replyTo.mediaType) return replyTo;
  // Otherwise enrich from the live messages array (by id).
  const found = (messages || []).find(m => m && m.id === replyTo.id);
  if (found) {
    return {
      id: found.id,
      body: found.body || '',
      type: (found.mediaType || found.type || 'text').toLowerCase(),
      mediaType: (found.mediaType || '').toLowerCase(),
      mediaUrl: found.mediaUrl || '',
      senderName: found.sentByName || (found.fromMe ? 'נציג' : 'לקוח'),
      fromMe: !!found.fromMe,
    };
  }
  return { ...replyTo, body: '(הודעה לא נמצאה)' };
};

const ReplyQuote = ({ replyTo, messages, onClickOriginal }) => {
  if (!replyTo || !replyTo.id) return null;

  const original = resolveOriginal(replyTo, messages);
  const isOutFromMe = !!original?.fromMe;
  const mediaKey = (original?.mediaType || original?.type || '').toLowerCase();
  const mediaLabel = MEDIA_LABELS[mediaKey];

  const preview = mediaLabel
    ? `${mediaLabel}${original?.body ? ` · ${truncate(original.body, 50)}` : ''}`
    : truncate(original?.body || '(הודעה ריקה)', 80);

  return (
    <button
      onClick={() => onClickOriginal && onClickOriginal(original?.id)}
      style={{
        width: '100%',
        display: 'block',
        textAlign: 'right',
        background: 'rgba(0,0,0,0.04)',
        borderRight: `4px solid ${isOutFromMe ? '#128c7e' : '#5b9bd5'}`,
        padding: '6px 10px',
        borderRadius: '6px',
        marginBottom: '6px',
        cursor: 'pointer',
        border: 'none',
        borderLeft: 'none',
        font: 'inherit',
      }}
      title="עבור להודעה המקורית"
    >
      <div style={{
        fontSize: '11px',
        fontWeight: 600,
        color: isOutFromMe ? '#128c7e' : '#1565c0',
        marginBottom: '2px',
      }}>
        {original?.senderName || (isOutFromMe ? 'נציג' : 'לקוח')}
      </div>
      <div style={{ fontSize: '12px', color: '#444', whiteSpace: 'pre-wrap' }}>
        {preview}
      </div>
      {original?.mediaUrl && mediaKey === 'image' && (
        <img
          src={original.mediaUrl}
          alt=""
          loading="lazy"
          style={{
            marginTop: '4px',
            maxHeight: '40px',
            maxWidth: '100%',
            borderRadius: '3px',
            objectFit: 'cover',
          }}
        />
      )}
    </button>
  );
};

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n) + '…' : (s || ''));

export default ReplyQuote;
